import React, { useState, useEffect } from "react";
import { DollarSign, Package, AlertTriangle, XCircle, Building2, MapPin, ArrowDownCircle, ArrowUpCircle, RotateCcw, ClipboardList, ShoppingCart, Loader2 } from "lucide-react";
import { inventoryApi } from "./inventoryApi";
import { formatCurrency, formatDateTime } from "./inventoryConstants";
import { useT } from "@/i18n/LanguageContext";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500 font-medium truncate">{label}</span>
      <Icon className={`w-4 h-4 shrink-0 ${accent}`} />
    </div>
    <div className="text-2xl font-bold text-slate-900 mt-2 tabular-nums truncate">{value}</div>
  </div>
);

export const InventoryDashboard = () => {
  const [data, setData] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useT();

  useEffect(() => {
    Promise.all([inventoryApi.getDashboard(), inventoryApi.getSpecializedValuation().catch(() => null)])
      .then(([d, v]) => { setData(d); setValuation(v); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!data) return <div className="text-center py-12 text-slate-400">Failed to load dashboard</div>;

  return (
    <div className="space-y-5" data-testid="inventory-dashboard">
      {/* Inventory Value breakdown */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 text-white">
        <div className="flex items-center gap-2 text-blue-100 text-sm mb-1"><DollarSign className="w-4 h-4" /> {t("Total Inventory Value")}</div>
        <div className="text-3xl sm:text-4xl font-bold tabular-nums truncate" data-testid="total-inventory-value">{formatCurrency(data.inventory_value)}</div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-4">
          <div className="bg-white/10 rounded-lg p-2 sm:p-2.5 min-w-0"><div className="text-[11px] sm:text-xs text-blue-100 truncate">{t("Actual")}</div><div className="text-sm sm:text-lg font-semibold leading-tight tabular-nums truncate">{formatCurrency(data.inventory_value_actual)}</div></div>
          <div className="bg-white/10 rounded-lg p-2 sm:p-2.5 min-w-0"><div className="text-[11px] sm:text-xs text-blue-100 truncate">{t("Estimated")}</div><div className="text-sm sm:text-lg font-semibold leading-tight tabular-nums truncate">{formatCurrency(data.inventory_value_estimated)}</div></div>
          <div className="bg-white/10 rounded-lg p-2 sm:p-2.5 min-w-0"><div className="text-[11px] sm:text-xs text-blue-100 truncate">{t("Verified")}</div><div className="text-sm sm:text-lg font-semibold leading-tight tabular-nums truncate">{formatCurrency(data.inventory_value_verified)}</div></div>
        </div>
        {valuation && (
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-blue-100">
            <span>{t("Tires:")} <span className="font-semibold text-white">{formatCurrency(valuation.tire_value)}</span> ({valuation.tire_count})</span>
            <span>{t("Salvage:")} <span className="font-semibold text-white">{formatCurrency(valuation.salvage_value)}</span> ({valuation.salvage_count})</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Package} label={t("Total Parts")} value={data.total_parts} accent="text-blue-500" testid="stat-total-parts" />
        <StatCard icon={AlertTriangle} label={t("Low Stock")} value={data.low_stock_count} accent="text-amber-500" testid="stat-low-stock" />
        <StatCard icon={XCircle} label={t("Out of Stock")} value={data.out_of_stock_count} accent="text-red-500" testid="stat-out-stock" />
        <StatCard icon={Building2} label={t("Vendors")} value={data.vendor_count} accent="text-slate-500" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 min-w-0" data-testid="dash-core-money-at-risk">
          <div className="flex items-center justify-between"><span className="text-xs text-red-700 font-medium truncate">{t("Core Money At Risk")}</span><RotateCcw className="w-4 h-4 text-red-500 shrink-0" /></div>
          <div className="text-2xl font-bold text-red-700 mt-2 tabular-nums truncate">{formatCurrency(data.core_money_at_risk || 0)}</div>
          <div className="text-xs text-red-500 mt-0.5 truncate">{data.open_cores || 0} {t("open core(s)")}</div>
        </div>
        <StatCard icon={ClipboardList} label={t("Open Requests")} value={data.open_requests || 0} accent="text-indigo-500" />
        <StatCard icon={AlertTriangle} label={t("Truck Down")} value={data.truck_down_requests || 0} accent="text-red-500" testid="stat-truck-down" />
        <StatCard icon={ShoppingCart} label={t("Open POs")} value={data.open_purchase_orders || 0} accent="text-blue-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Low stock list */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" /> {t("Low Stock Items")}</h3>
          {data.low_stock_items.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">{t("All stock levels healthy")}</p>
          ) : (
            <div className="space-y-0.5">
              {data.low_stock_items.map((p) => {
                const onHand = p.quantity_on_hand || 0;
                const reorder = p.reorder_point || 0;
                const oos = onHand <= 0;
                const pct = reorder > 0 ? Math.min(100, Math.max(onHand > 0 ? 6 : 0, (onHand / reorder) * 100)) : 0;
                return (
                  <div key={p.id} className="rounded-lg px-2.5 py-2 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-2.5">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${oos ? "bg-red-500" : "bg-amber-500"}`} />
                      <span className="flex-1 min-w-0 truncate text-sm">
                        <span className="font-medium text-slate-800">{p.name}</span>
                        {p.part_number && <span className="font-mono text-[11px] text-slate-400 ml-1.5">{p.part_number}</span>}
                      </span>
                      <span className={`shrink-0 text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full ${oos ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}>
                        {onHand} / {reorder}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 mt-1.5 ml-[18px] overflow-hidden">
                      <div className={`h-full rounded-full ${oos ? "bg-red-400" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-500 shrink-0" /> {t("Recent Activity")}</h3>
          {(data.recently_received?.length || 0) + (data.recently_issued?.length || 0) === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">{t("No recent activity")}</p>
          ) : (
            <div className="space-y-0.5">
              {[...(data.recently_received || []).map((x) => ({ ...x, _dir: "in" })), ...(data.recently_issued || []).map((x) => ({ ...x, _dir: "out" }))]
                .sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 8).map((tx) => {
                  const isIn = tx._dir === "in";
                  return (
                    <div key={tx.id} className="flex items-center gap-3 rounded-lg px-2.5 py-2 hover:bg-slate-50 transition-colors">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isIn ? "bg-green-50" : "bg-blue-50"}`}>
                        {isIn ? <ArrowDownCircle className="w-4 h-4 text-green-600" /> : <ArrowUpCircle className="w-4 h-4 text-blue-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{tx.part_name}</div>
                        <div className="text-[11px] text-slate-400 truncate">
                          <span className={isIn ? "text-green-600 font-medium" : "text-blue-600 font-medium"}>{isIn ? t("Received") : t("Issued")}</span>
                          {" · "}{formatDateTime(tx.date)}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
